<template>
  <div class="relative inline-block text-left" ref="dropdownRef">
    <!-- Trigger -->
    <div @click="toggle">
      <slot name="trigger">
        <BaseButton
          :variant="triggerVariant"
          :size="size"
          :disabled="disabled"
        >
          {{ triggerText }}
          <template #icon-right>
            <i
              :class="['fas fa-chevron-down text-xs transition-transform duration-200', isOpen ? 'rotate-180' : '']"
            ></i>
          </template>
        </BaseButton>
      </slot>
    </div>

    <!-- Dropdown Menu -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        :class="menuClasses"
        role="menu"
        aria-orientation="vertical"
      >
        <div class="py-1" role="none">
          <slot>
            <template v-for="(item, index) in items" :key="index">
              <!-- Divider -->
              <hr v-if="item.divider" class="my-1 border-gray-100" />

              <!-- Header -->
              <div
                v-else-if="item.header"
                class="px-3 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider"
              >
                {{ item.header }}
              </div>

              <!-- Regular Item -->
              <button
                v-else
                type="button"
                :class="[
                  'w-full flex items-center px-3 py-2 text-sm transition-colors',
                  item.disabled
                    ? 'text-gray-300 cursor-not-allowed'
                    : item.danger
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-gray-700 hover:bg-gray-50'
                ]"
                :disabled="item.disabled"
                role="menuitem"
                @click="handleItemClick(item)"
              >
                <span v-if="item.icon" class="mr-3 flex-shrink-0">
                  <component :is="item.icon" class="w-4 h-4" />
                </span>
                <span class="flex-1 text-left">{{ item.label }}</span>
                <span v-if="item.shortcut" class="ml-4 text-xs text-gray-400">
                  {{ item.shortcut }}
                </span>
              </button>
            </template>
          </slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import BaseButton from './BaseButton.vue'

export default {
  name: 'BaseDropdown',
  components: {
    BaseButton
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    triggerText: {
      type: String,
      default: 'Options'
    },
    triggerVariant: {
      type: String,
      default: 'ghost'
    },
    size: {
      type: String,
      default: 'md'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    position: {
      type: String,
      default: 'bottom-right',
      validator: (value) => ['bottom-left', 'bottom-right', 'top-left', 'top-right'].includes(value)
    },
    width: {
      type: String,
      default: 'auto',
      validator: (value) => ['auto', 'sm', 'md', 'lg', 'full'].includes(value)
    },
    closeOnClick: {
      type: Boolean,
      default: true
    }
  },
  emits: ['select', 'open', 'close'],
  data() {
    return {
      isOpen: false
    }
  },
  computed: {
    menuClasses() {
      return [
        'absolute z-50 mt-1 bg-white rounded-lg border border-gray-200 focus:outline-none overflow-hidden',
        this.positionClasses,
        this.widthClasses
      ]
    },
    positionClasses() {
      const positions = {
        'bottom-left': 'left-0 origin-top-left',
        'bottom-right': 'right-0 origin-top-right',
        'top-left': 'left-0 bottom-full mb-1 origin-bottom-left',
        'top-right': 'right-0 bottom-full mb-1 origin-bottom-right'
      }
      return positions[this.position]
    },
    widthClasses() {
      const widths = {
        auto: 'min-w-[10rem]',
        sm: 'w-40',
        md: 'w-52',
        lg: 'w-64',
        full: 'w-full'
      }
      return widths[this.width]
    }
  },
  methods: {
    toggle() {
      if (this.disabled) return
      this.isOpen = !this.isOpen
      this.$emit(this.isOpen ? 'open' : 'close')
    },
    open() {
      if (this.disabled) return
      this.isOpen = true
      this.$emit('open')
    },
    close() {
      this.isOpen = false
      this.$emit('close')
    },
    handleItemClick(item) {
      if (item.disabled) return

      if (item.action && typeof item.action === 'function') {
        item.action()
      }

      this.$emit('select', item)

      if (this.closeOnClick) {
        this.close()
      }
    },
    handleClickOutside(event) {
      if (this.$refs.dropdownRef && !this.$refs.dropdownRef.contains(event.target)) {
        this.close()
      }
    },
    handleEscape(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close()
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('keydown', this.handleEscape)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleEscape)
  }
}
</script>
